import { useEffect, useRef, useState } from 'react';

import DatePicker, { registerLocale } from 'react-datepicker';
import { Controller as ControllerBase } from 'react-hook-form';

import { setClass } from '../../helpers/utils';
import './RangeDatePicker.scss';
import 'react-datepicker/dist/react-datepicker.css';

import {
	RideGlCalendarOutline,
	RideGlWarningCircleSolid,
} from '@rimac-seguros/ride-system-components';
import { es } from 'date-fns/esm/locale';

registerLocale('es', es);

const Controller = ControllerBase as any;

type RangeDatepickerProps = {
	className?: string;
	type?: string;
	id?: string;
	name?: string;
	label?: string;
	placeholder?: string;
	disabled?: boolean;
	onBlur?: (...args: unknown[]) => void;
	onChange?: (...args: unknown[]) => void;
	onKeyDown?: (...args: unknown[]) => void;
	onClick?: (...args: unknown[]) => void;
	size?: string;
	minDate?: Date;
	maxDate?: Date;
	hookForm?: any;
	defaultValue?: Date | string | null;
	onCalendarOpen?: () => void;
	onCalendarClose?: () => void;
	showCalendarIcon?: boolean;
};

export const RangeDatepicker = ({
	className = '',
	type = 'text',
	id,
	name,
	label = '',
	placeholder = '',
	disabled = false,
	onBlur,
	onChange,
	onKeyDown,
	onClick,
	size = '',
	minDate,
	maxDate,
	hookForm = { formState: { errors: {} } } as any,
	defaultValue,
	onCalendarOpen = () => {},
	onCalendarClose = () => {},
	showCalendarIcon = true,
}: RangeDatepickerProps) => {
	const [isFocus, setIsFocus] = useState(false);
	const [isFill, setIsFill] = useState(false);
	const inputEl = useRef(null);

	const {
		control,
		watch,
		formState: { errors },
	} = hookForm;

	const typeSize = {
		medium: 'is-medium',
	};

	const isSize = typeSize[size] || '';

	const handleBlur = (event) => {
		setIsFocus(false);
		setIsFill(!isEmptyValue());

		if (typeof onBlur === 'function') {
			onBlur(event);
		}
	};

	const handleChange = (event) => {
		if (typeof onChange === 'function') {
			onChange(event);
		}
	};
	const watchInput = watch?.(name, true);
	const isEmptyValue = () => {
		const elem = inputEl.current;
		const input = elem.querySelector('input');
		return !(input?.value?.trim().length > 0);
	};

	const openDatePicker = () => {
		const elem = inputEl.current;
		const input = elem.querySelector('input');
		input.focus();
	};

	useEffect(() => {
		setIsFill(!isEmptyValue());
	}, [watchInput]);

	return (
		<div
			ref={inputEl}
			className={setClass(`range-datepicker icon-right
        ${isFill || isFocus ? 'is-fill' : ''}
        ${isFocus ? 'is-focused' : ''}
        ${isSize}
        ${disabled ? 'is-disabled' : ''}
        ${errors[name] ? 'is-error' : ''}
        ${className}`)}
		>
			{label ? (
				<div style={{ marginBottom: 6 }}>
					<span className="range-datepicker-label">{label}</span>
				</div>
			) : null}
			<div
				className="datepicker-row-outer"
				onClick={() => openDatePicker()}
				style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
			>
				<div className="datepicker-row-input">
					<Controller
						control={control}
						name={name}
						defaultValue={defaultValue}
						render={({ field: { onChange, onBlur, value, ref } }) => (
							<DatePicker
								selected={
									(value ?? defaultValue)
										? new Date(
												new Date(value ?? defaultValue).getTime() +
													5 * 60 * 60 * 1000,
											)
										: null
								}
								onChange={(e) => {
									onChange(e);
									handleChange(e);
								}}
								onBlur={(e) => handleBlur(e)}
								onFocus={() => setIsFocus(true)}
								dateFormat="dd/MM/yyyy"
								disabled={disabled}
								minDate={new Date(minDate)}
								maxDate={new Date(maxDate)}
								locale="es"
								onCalendarOpen={() => onCalendarOpen()}
								onCalendarClose={() => onCalendarClose()}
								className="input-datepicker-native"
								placeholderText={placeholder}
							/>
						)}
					/>
				</div>
				{showCalendarIcon && (
					<span className="button-calendar-outside">
						<RideGlCalendarOutline size="2x-large" />
					</span>
				)}
			</div>
			{errors[name] && (
				<div
					className="ride-error-message"
					role="alert"
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 6,
						marginTop: 7,
					}}
				>
					<span className="button-error">
						<RideGlWarningCircleSolid size="large" />
					</span>
					<span style={{ color: '#e53935', fontSize: 13 }}>
						{errors[name].message}
					</span>
				</div>
			)}
		</div>
	);
};
